/*
 * Live-mode clock — docked flush into the bottom-left corner of the
 * actual viewport (not the kiosk stage). Matches the dock language of
 * the back button (top-left) and KioskToggle (bottom-right): same 56
 * height, same glass treatment, only the inner-facing corner rounded.
 *
 * Renders one of three states:
 *   - idle       12-hour wall clock, e.g. "9:41 AM"
 *   - countdown  "Quiet time · 4:32" with an accent-orange wash
 *   - timer      "Story time · 2:14" with a softer wash
 *
 * Tap to open the ClockPicker (countdown / timer / alarms).
 */

/* Bottom-left pill holding the clock + the noise-meter sound icon as one box,
 * docked flush into the corner. */
.dock {
  position: fixed;
  left: 0;
  bottom: 0;
  z-index: 20;
  display: inline-flex;
  align-items: center;
  height: 56px;
  border: 1px solid rgba(255, 255, 255, 0.45);
  border-bottom: 0;
  border-left: 0;
  border-radius: 0 24px 0 0;
  background: rgba(255, 255, 255, 0.32);
  backdrop-filter: blur(14px) saturate(140%);
  -webkit-backdrop-filter: blur(14px) saturate(140%);
  color: var(--token-text-primary);
  font-family: var(--token-font-display);
  box-shadow:
    0 -8px 20px -10px rgba(0, 0, 0, 0.45),
    inset 0 1px 0 rgba(255, 255, 255, 0.55);
  user-select: none;
  overflow: hidden;
  transition:
    background 120ms ease,
    color 120ms ease,
    border-color 120ms ease;
}

/* The time / clock-picker segment of the pill. */
.timeBtn {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  align-self: stretch;
  padding: 0 12px 0 16px;
  border: 0;
  background: transparent;
  color: inherit;
  font: inherit;
  cursor: pointer;
  transition: background 120ms ease;
}

.timeBtn:hover {
  background: rgba(255, 255, 255, 0.22);
}

/* Hairline divider between the time and the sound icon. */
.sep {
  width: 1px;
  height: 26px;
  background: rgba(255, 255, 255, 0.4);
  flex-shrink: 0;
}

.clockCountdown .sep,
.clockTimer .sep {
  background: rgba(255, 255, 255, 0.5);
}

/* Active state: countdown running. Accent-orange wash so it pulls the
 * eye and reads as "something timed is happening". Pulsing border to
 * make sure the teacher notices when minutes are running low. */
.clockCountdown {
  background: var(--token-accent);
  border-color: var(--token-accent);
  color: #fff;
  box-shadow:
    0 -8px 20px -10px rgba(190, 110, 35, 0.6),
    inset 0 1px 0 rgba(255, 255, 255, 0.45);
}

.clockCountdown:hover {
  background: color-mix(in srgb, var(--token-accent) 85%, #000);
}

/* Active state: open-ended timer (stopwatch). Subtler than countdown
 * since it has no end — softer green accent so it doesn't compete. */
.clockTimer {
  background: rgba(89, 153, 88, 0.35);
  border-color: rgba(89, 153, 88, 0.55);
  color: var(--token-text-primary);
}

.clockTimer:hover {
  background: rgba(89, 153, 88, 0.55);
}

.label {
  font-family: var(--token-font-ui);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  opacity: 0.85;
  max-width: 140px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.clockCountdown .label,
.clockTimer .label {
  opacity: 0.95;
}

.time {
  font-size: 22px;
  font-weight: 700;
  letter-spacing: 0.5px;
  line-height: 1;
  font-variant-numeric: tabular-nums;
}

.meridiem {
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.6px;
  text-transform: uppercase;
  color: rgba(28, 38, 32, 0.65);
}

/* Small bell badge when alarms are scheduled — tucks into the right
 * side of the idle clock so the teacher knows alarms are armed. */
.alarmBadge {
  font-size: 14px;
  line-height: 1;
  margin-left: 4px;
  opacity: 0.7;
}
